import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { NgClass } from "@angular/common";

import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { ConfirmationService, MessageService } from "@openng/optimus-ui/api";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { first } from "rxjs";
import { ControllerCatalogService } from "../../services/api/controller-catalog/controller-catalog.service";
import { CONTROLLER_CATALOG_ITEM_TYPES, ControllerCatalogItemType } from "../../enums/controllerCatalogItemTypes";

import { ManufacturerComponent } from "./components/forms/manufacturer/manufacturer.component";
import { ModelComponent } from "./components/forms/model/model.component";
import { FaultListService } from "../../services/api/fault-list/fault-list.service";
import { FaultListComponent } from "./components/forms/fault-list/fault-list.component";

import { TableModule } from "@openng/optimus-ui/table";

import { FaultComponent } from "./components/forms/fault/fault.component";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { Column } from "../../models/ui/tableColumn";
import { SimplebarAngularModule } from "simplebar-angular";
import { ControllerCatalogItem } from "../../models/api/fault/controllerCatalogItem";
import { ControllerManufacturer } from "../../models/api/fault/controllerManufacturer";
import { ControllerModel } from "../../models/api/fault/controllerModel";
import { Fault } from "../../models/api/fault/fault";
import { FaultList } from "../../models/api/fault/faultList";
import { AuthService } from "../../services/api/auth/auth.service";
import { LanguageManagementComponent } from "./components/language-management/language-management.component";
import { ACTION_TYPE, ActionType } from "../../constants/actionTypes";
@Component({
  standalone: true,
  selector: "app-fault-allocation",
  imports: [
    SvgIconComponent,
    NgClass,
    TranslateModule,
    ButtonComponent,
    TableModule,
    SvgIconComponent,
    HasPermissionDirective,
    TimeoutComponent,
    ProgressSpinnerModule,
    SimplebarAngularModule,
  ],
  templateUrl: "./fault-allocation.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./fault-allocation.component.scss",
})
export class FaultAllocationComponent implements OnInit {
  private translate = inject(TranslateService);
  private controllerCatalog = inject(ControllerCatalogService);
  private faultListService = inject(FaultListService);
  private dialog = inject(DialogService);
  private message = inject(MessageService);
  private confirmation = inject(ConfirmationService);

  public permissions = inject(PermissionService);
  private auth = inject(AuthService);

  public role: string = this.auth.getRole();

  public controllerCatalogTypes = CONTROLLER_CATALOG_ITEM_TYPES;

  private faultListRef: DynamicDialogRef | null = null;

  public selectedManufacturer!: ControllerManufacturer;
  public selectedFaultList: WritableSignal<FaultList | null> = signal(null);

  public manufacturers!: ControllerManufacturer[];
  public loadingManufacturers = signal(true);
  public timeoutManufacturers = createTimeoutSignal(this.loadingManufacturers);

  public models!: ControllerModel[];
  public loadingModels = signal(true);
  public timeoutModels = createTimeoutSignal(this.loadingModels);

  public faultLists!: FaultList[];
  public loadingFaultLists = signal(true);
  public timeoutFaultLists = createTimeoutSignal(this.loadingFaultLists);

  public faultsCols: Column[] = [
    {
      field: "faultCode",
      header: "fault_allocation.fault_lists.faults.faultCode",
    },
    {
      field: "faultType",
      header: "fault_allocation.fault_lists.faults.faultType",
    },
  ];

  public ngOnInit() {
    this.getManufacturers();
    this.getFaultLists();
  }

  /**
   * Opens a given fix link in a new tab.
   * @param fixLink The URL of the fix link to open.
   */
  public openFixLink(fixLink: string) {
    window.open(fixLink, "_blank");
  }

  /**
   * Retrieves all available controller manufacturers from the controller catalog.
   * Sets the initial manufacturer selection to the first available manufacturer.
   */
  private getManufacturers() {
    this.loadingManufacturers.set(true);
    this.controllerCatalog
      .getCatalogItems(CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER)
      .pipe(first())
      .subscribe({
        next: (items) => {
          this.loadingManufacturers.set(false);
          if (items) {
            this.manufacturers = items;
            if (this.manufacturers.length > 0) {
              this.selectManufacturer(this.manufacturers[0]);
            }
          }
        },
        error: () => {
          this.loadingManufacturers.set(false);
        },
      });
  }

  /**
   * Retrieves all available controller models for a given manufacturer.
   * Updates the models list and loading state based on the subscription response.
   * @param manufacturerId - The ID of the manufacturer to fetch models for.
   */
  private getModels(manufacturerId: string) {
    this.loadingModels.set(true);
    this.controllerCatalog
      .getCatalogItems(CONTROLLER_CATALOG_ITEM_TYPES.MODEL, manufacturerId)
      .pipe(first())
      .subscribe({
        next: (items) => {
          this.loadingModels.set(false);
          if (items) {
            this.models = items;
          }
        },
        error: () => {
          this.loadingModels.set(false);
        },
      });
  }

  /**
   * Retrieves all fault lists from the service, updates the component state,
   * and optionally sets a selected fault list by index.
   * @param selectedIndex - Optional index to pre-select a fault list.
   */
  private getFaultLists(selectedIndex?: number) {
    this.loadingFaultLists.set(true);
    this.faultListService
      .getFaultLists()
      .pipe(first())
      .subscribe((faultLists) => {
        this.loadingFaultLists.set(false);
        this.faultLists = faultLists;
        this.selectedFaultList.set(null);
        if (selectedIndex !== undefined && selectedIndex > -1) {
          this.selectedFaultList.set(this.faultLists[selectedIndex]);
        }
      });
  }

  /**
   * Iterates over the manufacturers and sets the selected property to false for all except the
   * given manufacturer.
   * @param manufacturer - The manufacturer to keep selected.
   */
  private unselectOtherManufacturers(manufacturer: ControllerManufacturer) {
    this.manufacturers.forEach((m) => {
      if (m.id !== manufacturer.id) {
        m.selected = false;
      }
    });
  }

  /**
   * Sets the given manufacturer as selected and retrieves its available models.
   * Un-selects all other manufacturers.
   * @param manufacturer - The manufacturer to select.
   */
  public selectManufacturer(manufacturer: ControllerManufacturer) {
    manufacturer.selected = true;
    this.selectedManufacturer = manufacturer;
    this.unselectOtherManufacturers(manufacturer);
    if (manufacturer.id) {
      this.getModels(manufacturer.id);
    }
  }

  /**
   * Selects a fault list.
   * @param faultList - The fault list to select.
   */
  public selectFaultList(faultList: FaultList) {
    this.selectedFaultList.set(faultList);
  }

  /**
   * Opens a dialog for editing or adding a controller catalog item.
   * The dialog type is determined by the provided catalog type and action.
   * Upon closing the dialog, the item is processed based on the action type.
   * @param catalogType - The type of catalog (e.g., Manufacturer, Model).
   * @param actionType - The action to perform (e.g., add, edit).
   * @param catalogItem - The catalog item to be processed.
   */
  private openDialog(
    catalogType: ControllerCatalogItemType,
    actionType: ActionType,
    catalogItem: ControllerCatalogItem
  ) {
    this.faultListRef = this.dialog.open<ManufacturerComponent | ModelComponent>(this.getCatalogForm(catalogType), {
      header: this.getTitle(catalogType, actionType),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        actionType: actionType,
        controllerCatalogItem: catalogItem,
      },
      closable: true,
    });
    this.faultListRef?.onClose.subscribe({
      next: (data: ControllerCatalogItem) => {
        if (data) {
          this.onDialogClose(actionType, data, catalogType);
        }
      },
    });
  }

  /**
   * Retrieves a translated title string for a catalog form based on the catalog type and action type.
   * The translation is determined by the catalog type (e.g., Manufacturer, Model) and the action
   * type (e.g., add, edit, delete).
   *
   * @param catalogType - The type of the catalog (e.g., CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER).
   * @param actionType - The action to perform (e.g., "add", "edit", "delete").
   * @returns A translated string representing the title for the specified catalog form action.
   */
  private getTitle(catalogType: ControllerCatalogItemType, actionType: ActionType) {
    if (CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER === catalogType) {
      return this.translate.instant(`fault_allocation.controller_catalog.manufacturer_form.${actionType}`);
    } else if (CONTROLLER_CATALOG_ITEM_TYPES.MODEL === catalogType) {
      return this.translate.instant(`fault_allocation.controller_catalog.model_form.${actionType}`);
    }
  }

  /**
   * Closes the dialog and performs the action depending on the type.
   * @param actionType The type of action to perform (create or edit).
   * @param controllerCatalogItem The catalog item to add or edit.
   * @param catalogType The type of the catalog (e.g., Manufacturer, Model).
   */
  private onDialogClose(
    actionType: ActionType,
    controllerCatalogItem: ControllerCatalogItem,
    catalogType: ControllerCatalogItemType
  ) {
    if (actionType === ACTION_TYPE.ADD) {
      this.controllerCatalog
        .addCatalogItem(catalogType, controllerCatalogItem)
        .pipe(first())
        .subscribe(this.afterServerCommunicationCatalog(catalogType));
    } else if (actionType === ACTION_TYPE.EDIT) {
      this.controllerCatalog
        .editCatalogItem(catalogType, controllerCatalogItem)
        .pipe(first())
        .subscribe(this.afterServerCommunicationCatalog(catalogType));
    }
  }

  /**
   * Opens a dialog for adding a new controller catalog item.
   * The dialog type is determined by the provided catalog type.
   * The user needs to have the create permission to use this method.
   * @param controllerCatalogType The type of the catalog (e.g., Manufacturer, Model).
   */
  public onAddClick(controllerCatalogType: ControllerCatalogItemType) {
    if (this.permissions.hasPermission(this.permissions.allPermissions.FAULT_LIST_MAINT_CREATE)) {
      let emptyCatalogItem: ControllerCatalogItem;
      switch (controllerCatalogType) {
        case CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER:
          emptyCatalogItem = this.controllerCatalog.getEmptyCatalogItem(controllerCatalogType);
          break;
        case CONTROLLER_CATALOG_ITEM_TYPES.MODEL:
          emptyCatalogItem = this.controllerCatalog.getEmptyCatalogItem(
            controllerCatalogType,
            this.selectedManufacturer.id
          );
          break;
        default:
          emptyCatalogItem = this.controllerCatalog.getEmptyCatalogItem(controllerCatalogType);
      }
      this.openDialog(controllerCatalogType, ACTION_TYPE.ADD, emptyCatalogItem);
    }
  }

  /**
   * Retrieves the Angular component class corresponding to the given catalog type.
   * Depending on the catalog type, it returns either the ManufacturerComponent or the ModelComponent.
   * Defaults to ManufacturerComponent if the catalog type does not match any known types.
   *
   * @param catalogType - The type of the catalog (e.g., MANUFACTURER, MODEL).
   * @returns The Angular component class associated with the specified catalog type.
   */
  private getCatalogForm(catalogType: ControllerCatalogItemType) {
    switch (catalogType) {
      case CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER:
        return ManufacturerComponent;
      case CONTROLLER_CATALOG_ITEM_TYPES.MODEL:
        return ModelComponent;
      default:
        return ManufacturerComponent;
    }
  }

  /**
   * Opens a dialog for editing a controller catalog item.
   * The dialog is a {@link ManufacturerComponent} or {@link ModelComponent} with the given catalog item as data.
   * @param controllerCatalogType - The type of the catalog (e.g., MANUFACTURER, MODEL).
   * @param controllerCatalogItem - The catalog item to edit.
   * @param event - The event that triggered the edit action.
   */
  public onEditClick(
    controllerCatalogType: ControllerCatalogItemType,
    controllerCatalogItem: ControllerCatalogItem,
    event: Event
  ) {
    event.stopPropagation();
    this.openDialog(controllerCatalogType, ACTION_TYPE.EDIT, controllerCatalogItem);
  }

  /**
   * Opens a confirmation dialog for deleting a controller catalog item.
   * @param catalogType - The type of the catalog (e.g., MANUFACTURER, MODEL).
   * @param controllerCatalogItem - The catalog item to delete.
   * @param event - The event that triggered the delete action.
   */
  public onDeleteClick(
    catalogType: ControllerCatalogItemType,
    controllerCatalogItem: ControllerCatalogItem,
    event: Event
  ) {
    event.stopPropagation();
    this.confirmation.confirm({
      target: event.currentTarget as EventTarget,
      message: this.getTitle(catalogType, ACTION_TYPE.DELETE),
      key: "global",
      accept: () => {
        this.controllerCatalog
          .deleteCatalogItem(catalogType, controllerCatalogItem)
          .pipe(first())
          .subscribe(this.afterServerCommunicationCatalog(catalogType));
      },
    });
  }

  /**
   * After server communication callback for catalog actions.
   * @param catalogType The type of the catalog (e.g., MANUFACTURER, MODEL).
   * @returns An object with two callbacks: `next` and `error`.
   * `next` is called on success and refreshes the list of manufacturers or models.
   * `error` is called on failure and shows an error message.
   */
  private afterServerCommunicationCatalog(catalogType: ControllerCatalogItemType) {
    return {
      next: () => {
        if (catalogType === CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER) {
          this.getManufacturers();
        } else if (catalogType === CONTROLLER_CATALOG_ITEM_TYPES.MODEL && this.selectedManufacturer.id) {
          this.getModels(this.selectedManufacturer.id);
        }
      },
    };
  }

  /**
   * After server communication callback for fault list actions.
   * @param successMessage The success message to display if the action succeeds.
   * @returns An object with two callbacks: `next` and `error`.
   * `next` is called on success, refreshes the list of fault lists, and shows a success message.
   * `error` is called on failure, refreshes the list of fault lists, and shows an error message.
   */
  private afterServerCommunicationFaults(successMessage: string) {
    return {
      next: () => {
        const currentIndex = this.faultLists.findIndex((f: FaultList) => f.id === this.selectedFaultList()?.id);
        this.getFaultLists(currentIndex);
        this.message.add({
          key: "global",
          severity: "success",
          summary: this.translate.instant("common.success"),
          detail: successMessage,
          life: 4000,
        });
      },
      error: () => {
        const currentIndex = this.faultLists.findIndex((f: FaultList) => f.id === this.selectedFaultList()?.id);

        this.getFaultLists(currentIndex);
      },
    };
  }

  /**
   * Opens a dialog for adding or editing a fault list.
   * @param actionType The action to perform (add or edit).
   * @param faultList The fault list to add or edit.
   * The dialog is a {@link FaultListComponent} with the given fault list as data.
   * On close, the fault list is added or edited on the server.
   */
  private openFaultListDialog(actionType: ActionType, faultList: FaultList) {
    this.faultListRef = this.dialog.open(FaultListComponent, {
      header: this.translate.instant("fault_allocation.fault_lists.fault_list." + actionType),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        faultList,
      },
      closable: true,
    });
    this.faultListRef?.onClose.subscribe((faultList: FaultList) => {
      if (faultList) {
        if (actionType === ACTION_TYPE.ADD) {
          this.faultListService
            .postFaultList(faultList)
            .pipe(first())
            .subscribe(
              this.afterServerCommunicationFaults(
                this.translate.instant("fault_allocation.fault_lists.fault_list.success_add")
              )
            );
        } else if (actionType === ACTION_TYPE.EDIT) {
          this.faultListService
            .putFaultList(faultList)
            .pipe(first())
            .subscribe(
              this.afterServerCommunicationFaults(
                this.translate.instant("fault_allocation.fault_lists.fault_list.success_edit")
              )
            );
        }
      }
    });
  }

  /**
   * Initiates the process to add a new fault list by opening the fault list dialog
   * with an empty fault list. The dialog allows users to input details for the new fault list.
   */
  public onAddFaultList() {
    this.openFaultListDialog(ACTION_TYPE.ADD, this.faultListService.getEmptyFaultList());
  }

  /**
   * Opens the language management dialog.
   * The dialog allows sys admin to add, edit, and delete languages for the fault lists.
   */
  public onLanguageManagement() {
    this.dialog.open(LanguageManagementComponent, {
      header: this.translate.instant("fault_allocation.fault_lists.language_management.open_btn"),
      modal: true,
      contentStyle: { width: "50vw", "max-width": "800px", overflow: "auto" },
      closable: true,
    });
  }

  /**
   * Opens the fault list dialog with the given fault list for editing.
   * @param event - The event that triggered this call.
   * @param faultList - The fault list to edit.
   */
  public onEditFaultList(event: Event, faultList: FaultList) {
    event.stopPropagation();
    this.openFaultListDialog(ACTION_TYPE.EDIT, faultList);
  }

  /**
   * Opens a confirmation dialog for deleting the given fault list.
   * @param event - The event that triggered this call.
   * @param faultList - The fault list to delete.
   */
  public onDeleteFaultList(event: Event, faultList: FaultList) {
    event.stopPropagation();
    this.confirmation.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("fault_allocation.fault_lists.fault_list.delete"),
      key: "global",
      accept: () => {
        this.faultListService
          .deleteFaultList(faultList)
          .pipe(first())
          .subscribe(
            this.afterServerCommunicationFaults(
              this.translate.instant("fault_allocation.fault_lists.fault_list.success_delete")
            )
          );
      },
    });
  }

  /**
   * Opens a dialog for adding or editing a single fault.
   * The editor performs the API mutation itself so it can keep validation errors in the form.
   * On success it closes with the server response, which is then applied to local state here.
   */
  private openFaultDialog(actionType: ActionType, fault: Fault) {
    const selectedFaultList = this.selectedFaultList();
    if (!selectedFaultList?.id) {
      return;
    }

    const faultListId = String(selectedFaultList.id);
    this.faultListRef = this.dialog.open(FaultComponent, {
      header: this.translate.instant("fault_allocation.fault_lists.faults." + actionType),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        actionType,
        fault,
        faultListId,
      },
      closable: true,
    });
    this.faultListRef?.onClose.subscribe((serverFault: Fault) => {
      if (!serverFault) {
        return;
      }

      if (actionType === ACTION_TYPE.ADD) {
        this.appendFault(faultListId, serverFault);
      } else {
        this.replaceFault(faultListId, serverFault);
      }
    });
  }

  /**
   * Opens the fault dialog with an empty fault to add a new fault.
   * @remark This function is called when the user clicks the "Add fault" button.
   */
  public onAddFault() {
    if (!this.permissions.hasPermission(this.permissions.allPermissions.FAULT_LIST_CREATE)) {
      return;
    }

    const fault = {
      faultCode: "",
      faultType: "MAJOR",
      translations: [],
    };
    this.openFaultDialog(ACTION_TYPE.ADD, fault);
  }

  /**
   * Opens the fault dialog with the given fault for editing.
   * @param fault - The fault to edit.
   */
  public onEditFault(fault: Fault) {
    if (!this.canEditFault(fault)) {
      return;
    }

    this.openFaultDialog(ACTION_TYPE.EDIT, fault);
  }

  /**
   * Deletes a fault from the selected fault list.
   * @param event - The event that triggered this call.
   * @param fault - The fault to delete.
   * Opens a confirmation dialog before actually deleting the fault.
   * If confirmed, the fault is removed from the selected fault list and the list is updated on the server.
   */
  public onDeleteFault(event: Event, fault: Fault) {
    event.stopPropagation();
    const faultList = this.selectedFaultList();
    if (!faultList?.id || !fault.id || !this.canDeleteFault(fault)) {
      return;
    }

    const faultListId = String(faultList.id);
    const faultId = String(fault.id);
    this.confirmation.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("fault_allocation.fault_lists.faults.delete"),
      key: "global",
      accept: () => {
        this.faultListService
          .deleteFault(faultId)
          .pipe(first())
          .subscribe({
            next: () => this.removeFault(faultListId, faultId),
            error: (error) => this.showFaultMutationError(error),
          });
      },
    });
  }

  private canEditFault(fault: Fault): boolean {
    return (
      fault.isEditable === true && this.permissions.hasPermission(this.permissions.allPermissions.FAULT_LIST_UPDATE)
    );
  }

  private canDeleteFault(fault: Fault): boolean {
    return (
      fault.isEditable === true && this.permissions.hasPermission(this.permissions.allPermissions.FAULT_LIST_DELETE)
    );
  }

  private appendFault(faultListId: string, fault: Fault) {
    this.updateFaultListFaults(faultListId, (faults) => [...faults, fault]);
  }

  private replaceFault(faultListId: string, fault: Fault) {
    if (!fault.id) {
      return;
    }

    const faultId = String(fault.id);
    this.updateFaultListFaults(faultListId, (faults) =>
      faults.map((existingFault) => (String(existingFault.id) === faultId ? fault : existingFault))
    );
  }

  private removeFault(faultListId: string, faultId: string) {
    this.updateFaultListFaults(faultListId, (faults) =>
      faults.filter((existingFault) => String(existingFault.id) !== faultId)
    );
  }

  /**
   * Updates the selected list and the backing list collection together. The callback only runs
   * after a successful single-fault request, so failed mutations leave client state untouched.
   */
  private updateFaultListFaults(faultListId: string, updateFaults: (faults: Fault[]) => Fault[]) {
    const selectedFaultList = this.selectedFaultList();
    const currentFaultList =
      this.faultLists?.find((faultList) => String(faultList.id) === faultListId) ??
      (selectedFaultList && String(selectedFaultList.id) === faultListId ? selectedFaultList : null);

    if (!currentFaultList) {
      return;
    }

    // Do not pass faults to the constructor: it sorts them as a side effect, while
    // a successful create must append the server-returned fault to the local order.
    const updatedFaultList = new FaultList({ id: currentFaultList.id, label: currentFaultList.label });
    updatedFaultList.faults = updateFaults([...(currentFaultList.faults ?? [])]);

    this.faultLists = (this.faultLists ?? []).map((faultList) =>
      String(faultList.id) === faultListId ? updatedFaultList : faultList
    );

    if (selectedFaultList && String(selectedFaultList.id) === faultListId) {
      this.selectedFaultList.set(updatedFaultList);
    }
  }

  private showFaultMutationError(error: unknown) {
    const httpError = error as { error?: { code?: string; message?: string }; message?: string };
    const code = httpError.error?.code;
    const translationKey = code ? `error_messages.${code}` : "";
    const translatedDetail = translationKey
      ? this.translate.instant(translationKey, { object: this.translate.instant("common.entities.fault_list") })
      : "";

    this.message.add({
      key: "global",
      severity: "error",
      summary: this.translate.instant("common.server_error"),
      detail:
        translatedDetail && translatedDetail !== translationKey
          ? translatedDetail
          : httpError.error?.message || httpError.message || this.translate.instant("common.server_error"),
      life: 4000,
    });
  }
}
